import { ModuleWrapper } from '@vimeo/clip-ui/components/ModuleWrapper';
import { VideoDetailsBase, VideoDetailsSkeleton } from '@vimeo/clip-ui/components/VideoDetails';
import UploaderDetails from '@vimeo/clip-ui/components/VideoDetails/components/UploaderDetails';
import { PrivacyUtility } from '@vimeo/clip-ui/utils/privacyUtility';
import { timeBetween } from '@vimeo/core/libs/date';
import { getUploaderDetailsDisplayConfig } from '@vimeo/viewer-showcase-ui/utils/constants';
import {
  getVideoDetailsDisplayConfig,
  getViewsString,
} from '@vimeo/viewer-showcase-ui/utils/helper';

import { useGetShowcase } from '../hooks/use-get-showcase';
import { useGetShowcaseVideo } from '../hooks/use-get-showcase-video';
import { useGetShowcaseVideoUserTotalVideos } from '../hooks/use-get-showcase-video-user-total-videos';
import { useLiveEventData } from '../hooks/use-live-event-data';
import { useSelectedId } from '../hooks/use-selected-id';
import { useVideoCredits } from '../hooks/use-video-credits';

interface Props extends React.PropsWithChildren {
  enableLike: boolean;
  showDateAdded: boolean;
  showDescription: boolean;
  showProfileImage: boolean;
  showProfileName: boolean;
  showTitle: boolean;
  showViews: boolean;
}

export const VideoDetailsModule = ({
  children,
  enableLike,
  showDateAdded,
  showDescription,
  showProfileImage,
  showProfileName,
  showTitle,
  showViews,
}: Props) => {
  const { selectedId, isLiveEvent } = useSelectedId();
  const { clip, isLoading: clipIsLoading } = useGetShowcaseVideo(selectedId);
  const { liveStatus } = useLiveEventData();
  const { colorMode } = useGetShowcase();
  const credits = useVideoCredits();
  const privacy = clip?.privacy;
  const isPublic = !!privacy?.view && PrivacyUtility.isPublicFacingPrivacy(privacy?.view);

  const { dateAdded, description, like, owner, portrait, views } = clip?.page || {};

  // Only show properties when enabled from both clip and showcase settings
  const videoDetailsDisplayConfig = getVideoDetailsDisplayConfig({
    showLike: like && enableLike,
    showDateAdded: dateAdded && showDateAdded,
    showDescription: description && showDescription,
    showTitle,
    showViews: views && showViews,
  });
  const uploaderDetailsDisplayConfig = getUploaderDetailsDisplayConfig({
    showByline: (isPublic || owner) && showProfileName,
    showProfile: (isPublic || portrait) && showProfileImage,
  });

  const {
    onGetTotalVideos,
    data: totalVideosData,
    error: totalVideosError,
    loading: isTotalVideosLoading,
  } = useGetShowcaseVideoUserTotalVideos(selectedId);

  const onProfileMouseOver = () => {
    // if the lazy GET Video is called
    if (totalVideosData || totalVideosError || isTotalVideosLoading) return;

    onGetTotalVideos();
  };

  if (!selectedId || clipIsLoading)
    return (
      <ModuleWrapper>
        <VideoDetailsSkeleton />
      </ModuleWrapper>
    );

  if (!clip) return null;

  const {
    createdTime,
    language,
    name,
    descriptionHtml,
    user,
    isRatedMature,
    is360Video,
    license,
    categories,
    isDolbyVision,
    isHdr10,
    isHdr10Plus,
    aiContent,
    isAdvert,
    stats,
  } = clip;

  const timeSinceCreated = createdTime ? timeBetween(new Date(createdTime), new Date()) : '';
  const viewsString = getViewsString(stats?.plays);
  let userWithTotalVideos = { ...user };
  const publicVideos = totalVideosData?.user?.metadata?.publicVideos;

  if (publicVideos && user) {
    userWithTotalVideos = {
      ...user,
      metadata: { ...user?.metadata, publicVideos },
    };
  }

  return (
    <>
      <ModuleWrapper
        sx={{
          color: colorMode === 'dark' ? 'white' : 'black',
        }}
      >
        <VideoDetailsBase
          createdTime={createdTime}
          language={language}
          title={name}
          description={descriptionHtml ?? undefined}
          isRatedMature={isRatedMature}
          is360Video={is360Video}
          creativeCommonsDescriptor={license}
          credits={credits}
          categories={categories}
          isDolbyVision={isDolbyVision}
          isHdr10={isHdr10}
          isHdr10Plus={isHdr10Plus}
          aiContent={aiContent}
          isAdvert={isAdvert}
          isStreaming={liveStatus === 'streaming'}
          isLiveEvent={isLiveEvent}
          timeSinceCreatedString={timeSinceCreated}
          viewsString={viewsString}
          {...videoDetailsDisplayConfig}
        >
          {children}
          <UploaderDetails
            user={userWithTotalVideos}
            videoId={selectedId}
            onProfileMouseOver={onProfileMouseOver}
            {...uploaderDetailsDisplayConfig}
          />
        </VideoDetailsBase>
      </ModuleWrapper>
    </>
  );
};
